import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PartyPopper, Rocket, Crown, TrendingUp, ArrowRight, Star, Sparkles, Home, Image, Video, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionTier, TIER_CONFIGS } from '@/types/billing';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: SubscriptionTier;
  limitType: 'listings' | 'photoEdits' | 'virtualStaging' | 'aiAdditions';
  currentCount: number;
  limit: number;
}

const limitTypeLabels: Record<UpgradePromptProps['limitType'], { singular: string; plural: string; icon: React.ElementType }> = {
  listings: { singular: 'property listing', plural: 'property listings', icon: Home },
  photoEdits: { singular: 'photo edit', plural: 'photo edits', icon: Image },
  virtualStaging: { singular: 'virtually staged property', plural: 'virtually staged properties', icon: Sparkles },
  aiAdditions: { singular: 'AI addition', plural: 'AI additions', icon: Sparkles },
};

const tierUpgrades: Record<SubscriptionTier, SubscriptionTier | null> = {
  free: 'starter',
  starter: 'growth',
  growth: 'studio',
  studio: null, // Already at max
  one_off_kit: 'starter',
};

const tierIcons: Record<SubscriptionTier, React.ElementType> = {
  free: Sparkles,
  starter: TrendingUp,
  growth: Rocket,
  studio: Crown,
  one_off_kit: Star,
};

export function UpgradePrompt({ 
  isOpen, 
  onClose, 
  currentTier, 
  limitType, 
  currentCount, 
  limit 
}: UpgradePromptProps) {
  const navigate = useNavigate();
  const labels = limitTypeLabels[limitType];
  const LimitIcon = labels.icon;
  const nextTier = tierUpgrades[currentTier];
  const nextTierConfig = nextTier ? TIER_CONFIGS[nextTier] : null;
  const NextTierIcon = nextTier ? tierIcons[nextTier] : Rocket;

  // Get the new limit for the next tier
  const getNextTierLimit = () => {
    if (!nextTierConfig) return 'unlimited';
    switch (limitType) {
      case 'listings':
        return nextTierConfig.activeListingsLimit === null ? 'unlimited' : nextTierConfig.activeListingsLimit;
      case 'photoEdits':
        return nextTierConfig.monthlyLimits.photoEdits === null ? 'unlimited' : nextTierConfig.monthlyLimits.photoEdits;
      case 'virtualStaging':
        return nextTierConfig.monthlyLimits.virtualStagingProperties;
      case 'aiAdditions':
        return nextTierConfig.monthlyLimits.aiAdditions;
      default:
        return 0;
    }
  };

  const nextLimit = getNextTierLimit();

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center">
            <PartyPopper className="w-8 h-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-display">
            You're on a Roll! 🎉
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            Congratulations on creating {currentCount} {currentCount === 1 ? labels.singular : labels.plural}! 
            You've reached your plan's limit of {limit}.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <NextTierIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    {nextTierConfig ? `Upgrade to ${nextTierConfig.displayName}` : 'You\'re at the Top!'}
                  </CardTitle>
                  <CardDescription>
                    {nextTierConfig 
                      ? `$${nextTierConfig.priceCents / 100}/month`
                      : 'You have our best plan'
                    }
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {nextTierConfig ? (
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <LimitIcon className="w-4 h-4 text-primary" />
                    <span>
                      {typeof nextLimit === 'string' 
                        ? `${nextLimit.charAt(0).toUpperCase() + nextLimit.slice(1)} ${labels.plural}`
                        : `Up to ${nextLimit} ${labels.plural}`
                      }
                    </span>
                  </li>
                  {nextTierConfig.features.slice(0, 3).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Star className="w-3 h-3" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Contact us for custom enterprise solutions with higher limits.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {nextTierConfig ? (
            <>
              <Button onClick={handleUpgrade} className="w-full gap-2">
                View Upgrade Options
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button variant="ghost" onClick={onClose} className="w-full">
                Maybe Later
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => { onClose(); navigate('/contact'); }} className="w-full gap-2">
                Contact Sales
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button variant="ghost" onClick={onClose} className="w-full">
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Inline banner version for softer prompting
interface UpgradeBannerProps {
  currentTier: SubscriptionTier;
  limitType: UpgradePromptProps['limitType'];
  currentCount: number;
  limit: number;
  className?: string;
}

export function UpgradeBanner({ currentTier, limitType, currentCount, limit, className }: UpgradeBannerProps) {
  const navigate = useNavigate();
  const labels = limitTypeLabels[limitType];
  const remaining = Math.max(0, limit - currentCount);
  const isNearLimit = remaining <= Math.ceil(limit * 0.2); // Within 20% of limit
  const isAtLimit = remaining === 0;

  if (!isNearLimit && !isAtLimit) return null;

  return (
    <Card className={`border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 ${className}`}>
      <CardContent className="py-3 px-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            {isAtLimit ? (
              <PartyPopper className="w-4 h-4 text-amber-600" />
            ) : (
              <Rocket className="w-4 h-4 text-amber-600" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium">
              {isAtLimit 
                ? `You've used all ${limit} ${labels.plural}!` 
                : `Only ${remaining} ${remaining === 1 ? labels.singular : labels.plural} left`
              }
            </p>
            <p className="text-xs text-muted-foreground">
              {isAtLimit 
                ? 'Upgrade to keep the momentum going' 
                : 'Upgrade for more capacity'
              }
            </p>
          </div>
        </div>
        <Button 
          size="sm" 
          variant={isAtLimit ? 'default' : 'outline'}
          onClick={() => navigate('/pricing')}
          className="shrink-0"
        >
          Upgrade
        </Button>
      </CardContent>
    </Card>
  );
}

// Simple inline warning for forms
interface LimitWarningProps {
  remaining: number;
  limit: number;
  itemLabel: string;
}

export function LimitWarning({ remaining, limit, itemLabel }: LimitWarningProps) {
  if (remaining > Math.ceil(limit * 0.3)) return null; // Only show when within 30%
  
  const isAtLimit = remaining === 0;
  
  return (
    <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
      isAtLimit 
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
        : 'bg-muted text-muted-foreground'
    }`}>
      {isAtLimit ? (
        <PartyPopper className="w-4 h-4" />
      ) : (
        <Rocket className="w-4 h-4" />
      )}
      <span>
        {isAtLimit 
          ? `Great progress! You've reached your ${itemLabel} limit.`
          : `${remaining} ${itemLabel}${remaining === 1 ? '' : 's'} remaining this month`
        }
      </span>
    </div>
  );
}
